import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  RefreshCw, TrendingDown, Search, Filter, ChevronDown, ChevronUp,
  ExternalLink, Bookmark, EyeOff, Fuel, Gauge, Calendar, MapPin,
  AlertCircle, Sparkles, Car, ArrowDownRight,
  ThumbsUp, ThumbsDown, CheckCircle2, XCircle, X, Zap, ShieldCheck
} from 'lucide-react';
import { opportunityApi } from '../services/opportunityApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPrice, formatMileage } from '../utils/formatters';

// ── Seller type pill ──────────────────────────────────────────────
const SellerPill = ({ type }) => {
  const config = type === 'private'
    ? { label: 'Particulier', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' }
    : { label: 'Professionnel', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${config.cls}`}>
      {config.label}
    </span>
  );
};

// ── Spec pill (reusable) ──────────────────────────────────────────
const SpecPill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center rounded-full bg-dark-hover border border-dark-border px-2 py-0.5 text-[11px] text-secondary">
    {Icon && <Icon className="h-3 w-3 mr-1 text-muted" />}
    {children}
  </span>
);

// ── Score bar (horizontal progress) ─────────────────────────────
const ScoreBar = ({ label, score, tooltip }) => {
  if (score == null) return null;
  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 70 ? 'text-emerald-400' : score >= 40 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex items-center gap-2" title={tooltip}>
      <span className="text-[11px] text-muted w-20 shrink-0 cursor-help">{label}</span>
      <div className="flex-1 h-2 bg-dark-hover rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-bold w-7 text-right ${textColor}`}>{score}</span>
    </div>
  );
};

// ── Feedback tags ───────────────────────────────────────────────
const FEEDBACK_TAGS = [
  { id: 'price_too_high', label: 'Prix trop eleve' },
  { id: 'too_many_km', label: 'Trop de km' },
  { id: 'too_old', label: 'Trop ancienne' },
  { id: 'wrong_brand', label: 'Pas ma marque' },
  { id: 'wrong_segment', label: 'Pas mon segment' },
  { id: 'bad_condition', label: 'Mauvais etat' },
];

// ── Profile card ──────────────────────────────────────────────────
const ProfileCard = ({ profile, collapsed, onToggle }) => {
  if (!profile) return null;

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-hover transition-colors"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="h-4 w-4 text-carmin" />
          <span className="text-sm font-medium text-primary">Profil d'achat</span>
          <span className="text-xs text-muted">
            {profile.source === 'sales_history'
              ? `Base sur ${profile.sold_count} ventes`
              : 'Base sur le stock actuel'}
          </span>
        </div>
        {collapsed ? <ChevronDown className="h-4 w-4 text-muted" /> : <ChevronUp className="h-4 w-4 text-muted" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-4 flex flex-wrap gap-2 border-t border-dark-border pt-3">
          {profile.makes?.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-muted">Marques:</span>
              {profile.makes.map(m => (
                <span key={m} className="px-2 py-0.5 rounded-full bg-carmin/10 border border-carmin/20 text-xs text-carmin font-medium">{m}</span>
              ))}
            </div>
          )}
          {profile.price_min && profile.price_max && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted">Prix:</span>
              <span className="text-xs text-secondary">{formatPrice(profile.price_min)} - {formatPrice(profile.price_max)}</span>
            </div>
          )}
          {profile.fuel_types?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted">Carburant:</span>
              {profile.fuel_types.map(f => (
                <span key={f} className="text-xs text-secondary capitalize">{f}</span>
              ))}
            </div>
          )}
          {profile.year_min && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted">Depuis:</span>
              <span className="text-xs text-secondary">{profile.year_min}</span>
            </div>
          )}
          {profile.rotation_by_segment && Object.keys(profile.rotation_by_segment).length > 0 && (
            <div className="w-full mt-1">
              <span className="text-[11px] text-muted">Rotation moy.:</span>
              <span className="ml-1">
                {Object.entries(profile.rotation_by_segment).map(([seg, days]) => (
                  <span key={seg} className="text-xs bg-dark-hover px-1.5 py-0.5 rounded text-secondary ml-1">{seg}: {days}j</span>
                ))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Opportunity row (full-width horizontal layout) ─────────────
const OpportunityRow = ({ opp, onDismiss, onSave, onFeedback }) => {
  const v = opp.vehicle || {};
  const p = opp.pricing || {};
  const o = opp.opportunity || {};
  const seller = opp.seller || {};
  const images = opp.images || [];
  const isDismissed = opp.user_action === 'dismissed';
  const isSaved = opp.user_action === 'saved';

  // Days online
  const msOnline = opp.listed_since ? Date.now() - new Date(opp.listed_since).getTime() : null;
  const daysOnline = msOnline != null ? Math.floor(msOnline / 86400000) : null;
  const hoursOnline = msOnline != null ? Math.floor(msOnline / 3600000) : null;
  const daysLabel = daysOnline == null ? null : daysOnline >= 1 ? `${daysOnline}j en ligne` : hoursOnline >= 1 ? `${hoursOnline}h en ligne` : '< 1h';

  // Feedback state
  const [feedbackState, setFeedbackState] = useState(
    opp.user_feedback === 'good' ? 'good' :
    opp.user_feedback === 'bad' ? 'bad_submitted' : null
  );
  const [selectedTags, setSelectedTags] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState('');

  const handleThumbsUp = () => {
    setFeedbackState('good');
    onFeedback(opp.id, {
      rating: 'good',
      vehicle_snapshot: { make: v.make, model: v.model, year: v.year, price: p.advertised_price, mileage: v.mileage, fuel_type: v.fuel_type, body_type: v.body_type },
    });
  };

  const handleThumbsDown = () => {
    setFeedbackState('bad_pending');
  };

  const handleSubmitBad = () => {
    if (selectedTags.length === 0) return;
    setFeedbackState('bad_submitted');
    onFeedback(opp.id, {
      rating: 'bad',
      tags: selectedTags,
      comment: feedbackComment || null,
      vehicle_snapshot: { make: v.make, model: v.model, year: v.year, price: p.advertised_price, mileage: v.mileage, fuel_type: v.fuel_type, body_type: v.body_type },
    });
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(t => t !== tagId) : [...prev, tagId]
    );
  };

  const sellerBehavior = opp.seller_behavior;

  return (
    <div className={`bg-dark-card border border-dark-border rounded-xl overflow-hidden transition-all ${isDismissed ? 'opacity-40' : ''}`}>
      <div className="flex flex-col md:flex-row">

        {/* Left: Image */}
        <div className="relative w-full md:w-52 lg:w-64 shrink-0 aspect-[4/3] md:aspect-auto bg-dark-surface overflow-hidden">
          {images.length > 0 ? (
            <img
              src={images[0]}
              alt={`${v.make} ${v.model}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[160px]">
              <Car className="h-12 w-12 text-muted/30" />
            </div>
          )}
        </div>

        {/* Middle: Info + Prix */}
        <div className="flex-1 p-4 space-y-3 min-w-0">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-primary leading-tight">
                {v.make} {v.model} {v.year}
              </h3>
              {o.discount_pct && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  <ArrowDownRight className="h-3 w-3" />
                  -{Math.round(o.discount_pct)}%
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              {seller.type && <SellerPill type={seller.type} />}
              {(seller.city || seller.location) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {seller.city || seller.location}{seller.zip ? ` (${seller.zip})` : ''}
                </span>
              )}
              {daysLabel && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {daysLabel}
                </span>
              )}
            </div>
          </div>

          {/* Specs */}
          <div className="flex flex-wrap gap-1.5">
            {v.mileage && <SpecPill icon={Gauge}>{formatMileage(v.mileage)}</SpecPill>}
            {v.fuel_type && <SpecPill icon={Fuel}>{v.fuel_type}</SpecPill>}
            {v.body_type && <SpecPill>{v.body_type}</SpecPill>}
            {v.transmission && <SpecPill>{v.transmission}</SpecPill>}
            {v.power_kw && <SpecPill>{v.power_kw} kW</SpecPill>}
          </div>

          {/* Saturation warning */}
          {opp.saturation && opp.saturation.level !== 'none' && (
            <div className="flex items-center gap-1 text-xs text-amber-400 bg-amber-400/10 rounded px-2 py-1 w-fit">
              <AlertCircle className="h-3 w-3" />
              <span>{opp.saturation.similar_count} {opp.saturation.similar_make}{opp.saturation.segment ? ` (${opp.saturation.segment})` : ''} deja en stock</span>
            </div>
          )}

          {/* Price block */}
          {opp.dealer_analysis ? (() => {
            const da = opp.dealer_analysis;
            // Régime de la marge (BE): TVA 21% sur la marge, marge HTVA = marge × 100/121
            const grossMarginHtva = da.gross_margin * 100 / 121;
            const grossMarginHtvaPct = da.resale_price ? (grossMarginHtva / da.resale_price * 100) : 0;
            const verdictColor = da.verdict === 'excellent' ? 'text-emerald-400' : da.verdict === 'acceptable' ? 'text-amber-400' : 'text-red-400';
            return (
              <div className="bg-dark-hover rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] uppercase tracking-wider text-muted">Prix annonce</span>
                  <span className="text-base font-bold text-primary">{formatPrice(da.advertised_price)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] uppercase tracking-wider text-muted" title="Fourchette de prix sur AutoScout24 pour ce type de vehicule">Marche B2C</span>
                  <span className="text-sm text-secondary">
                    {da.market_min && da.market_max
                      ? `${formatPrice(da.market_min)} - ${formatPrice(da.market_max)}`
                      : formatPrice(da.resale_price)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1 border-t border-dark-border">
                  <span className={`text-[10px] uppercase tracking-wider font-medium ${verdictColor}`} title="Marge apres TVA sur marge (21%)">Marge brute HTVA</span>
                  <span className={`text-sm font-bold ${verdictColor}`}>+{formatPrice(Math.round(grossMarginHtva))} ({grossMarginHtvaPct.toFixed(1)}%)</span>
                </div>
                {da.max_purchase_price < da.advertised_price && (
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-muted" title="Prix maximum d'achat pour atteindre votre objectif de marge">Achat max @ {da.target_margin_pct}%</span>
                  <span className="text-secondary">{formatPrice(da.max_purchase_price)}</span>
                </div>
                )}
              </div>
            );
          })() : (
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted">Prix annonce</span>
                <p className="text-lg font-bold text-primary">{formatPrice(p.advertised_price)}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted">Marche</span>
                <p className="text-sm text-secondary">{formatPrice(p.estimated_medium)}</p>
              </div>
              {o.discount_amount > 0 && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400" title="Marge apres TVA sur marge (21%)">Profit estime HTVA</span>
                  <p className="text-sm font-bold text-emerald-400">+{formatPrice(Math.round(o.discount_amount * 100 / 121))}</p>
                </div>
              )}
            </div>
          )}

          {/* Historical validation badge */}
          {opp.historical_validation?.status && (
            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded w-fit ${
              opp.historical_validation.status === 'validated' ? 'bg-emerald-500/10 text-emerald-400' :
              opp.historical_validation.status === 'divergent' ? 'bg-red-500/10 text-red-400' :
              'bg-zinc-500/10 text-zinc-400'
            }`}>
              <ShieldCheck className="h-3 w-3" />
              <span>{opp.historical_validation.status === 'validated' ? 'Valide' : opp.historical_validation.status === 'divergent' ? 'Divergent' : 'Donnees limitees'}</span>
            </div>
          )}

          {/* Market estimate B2C dealer range */}
          {opp.market_estimate?.b2c_dealer && (
            <div className="flex items-center gap-1.5 text-xs text-secondary">
              <span className="text-muted">B2C dealer:</span>
              <span>{formatPrice(opp.market_estimate.b2c_dealer.low)} - {formatPrice(opp.market_estimate.b2c_dealer.high)}</span>
            </div>
          )}

          {/* Similar listings count (from AutoLink when available) */}
          {(opp.similar_listings?.count || opp.similar_listings_count) > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-secondary">
              <Search className="h-3 w-3 text-muted" />
              <span>{opp.similar_listings?.count || opp.similar_listings_count} vehicule{(opp.similar_listings?.count || opp.similar_listings_count) > 1 ? 's' : ''} similaire{(opp.similar_listings?.count || opp.similar_listings_count) > 1 ? 's' : ''} en ligne</span>
            </div>
          )}

          {/* Price drops */}
          {opp.price_drops && opp.price_drops.num_drops > 0 && (
            <div className="text-xs text-green-400">
              <div className="flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                <span>{opp.price_drops.num_drops} baisse{opp.price_drops.num_drops > 1 ? 's' : ''} · -{opp.price_drops.total_drop_pct?.toFixed(1)}% ({opp.price_drops.total_drop?.toLocaleString('fr-BE')} €)</span>
              </div>
              {opp.price_drops.history?.length > 0 && (
                <div className="mt-1.5 ml-0.5 border-l border-green-500/20 pl-2.5 space-y-0.5">
                  {opp.price_drops.history.map((drop, i) => (
                    <div key={i} className="text-[11px] text-secondary">
                      <span className="text-green-400">J+{drop.days_since_listing}</span> · {drop.old_price?.toLocaleString('fr-BE')} → {drop.new_price?.toLocaleString('fr-BE')} €
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Seller behavior (shown when AutoLink data available, pro sellers only) */}
          {sellerBehavior && sellerBehavior.confidence !== 'low' && sellerBehavior.behavior && (
            <div className="text-xs bg-dark-hover rounded-lg px-3 py-2">
              <span className="text-muted">Comportement vendeur : </span>
              <span className={
                sellerBehavior.behavior.pattern === 'progressive' ? 'text-green-400' :
                sellerBehavior.behavior.pattern === 'single_drop' ? 'text-amber-400' :
                'text-secondary'
              }>
                {sellerBehavior.behavior.pattern === 'firm' && 'Prix ferme — rarement de baisse'}
                {sellerBehavior.behavior.pattern === 'single_drop' && `Baisse unique apres ~${sellerBehavior.behavior.avg_days_before_first_drop}j`}
                {sellerBehavior.behavior.pattern === 'progressive' && `Baisses progressives — moy. -${sellerBehavior.behavior.avg_total_drop_pct?.toFixed(1)}%`}
              </span>
              {sellerBehavior.total_listings_analyzed && (
                <span className="text-muted"> ({sellerBehavior.total_listings_analyzed} annonces)</span>
              )}
            </div>
          )}
        </div>

        {/* Right: Scores + Actions */}
        <div className="w-full md:w-56 lg:w-64 shrink-0 p-4 md:border-l border-t md:border-t-0 border-dark-border flex flex-col gap-4">
          {/* Scores */}
          {opp.scores && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted mb-2">Scores /100</p>
              <div className="space-y-1.5">
                <ScoreBar label="Marge nette" score={opp.scores.profit} tooltip="Potentiel de profit apres couts (reconditionnement, frais fixes). Plus le prix est bas vs l'estimation marche, plus le score est eleve." />
                <ScoreBar label="Revente" score={opp.scores.velocity} tooltip="Facilite de revente basee sur la rotation du segment, la demande pour cette marque et la saturation de votre stock." />
                <ScoreBar label="Negociable" score={opp.scores.motivation} tooltip="Probabilite que le vendeur accepte une negociation. Base sur le temps en ligne, les baisses de prix deja faites, et le type de vendeur (particulier = plus flexible)." />
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="space-y-2">
            <a
              href={opp.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 h-9 rounded-lg bg-carmin text-white text-xs font-medium hover:bg-carmin-light transition-colors w-full"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Voir l'annonce
            </a>
            <div className="flex gap-2">
              <button
                onClick={() => onSave(opp.id)}
                className={`flex-1 h-8 flex items-center justify-center gap-1 rounded-lg border text-xs transition-colors ${
                  isSaved
                    ? 'bg-carmin/10 border-carmin/30 text-carmin'
                    : 'border-dark-border text-muted hover:text-primary'
                }`}
              >
                <Bookmark className="h-3.5 w-3.5" />
                {isSaved ? 'Sauvegardee' : 'Sauvegarder'}
              </button>
              <button
                onClick={() => onDismiss(opp.id)}
                className="h-8 px-3 flex items-center justify-center rounded-lg border border-dark-border text-muted hover:text-red-400 hover:border-red-500/30 text-xs transition-colors"
              >
                <EyeOff className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Feedback */}
          <div className="border-t border-dark-border pt-3">
            {feedbackState === null && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted mb-2">Cette suggestion</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleThumbsUp}
                    className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500/15 hover:border-emerald-500/40 text-xs font-medium transition-colors"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    Bonne
                  </button>
                  <button
                    onClick={handleThumbsDown}
                    className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400 hover:bg-red-500/15 hover:border-red-500/40 text-xs font-medium transition-colors"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                    Mauvaise
                  </button>
                </div>
              </div>
            )}
            {feedbackState === 'good' && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                Merci pour votre avis
              </div>
            )}
            {feedbackState === 'bad_submitted' && (
              <div className="flex items-center gap-1.5 text-xs text-red-400">
                <XCircle className="h-4 w-4" />
                Avis enregistre
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Negative feedback modal */}
      {feedbackState === 'bad_pending' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setFeedbackState(null); setSelectedTags([]); setFeedbackComment(''); }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-dark-card border border-dark-border rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setFeedbackState(null); setSelectedTags([]); setFeedbackComment(''); }}
              className="absolute top-4 right-4 text-muted hover:text-primary transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-carmin" />
              <h3 className="text-sm font-semibold text-primary">Ameliorez vos suggestions</h3>
            </div>
            <p className="text-xs text-muted mb-4">
              Votre retour entraine l'algorithme a mieux comprendre vos besoins et affiner les prochaines opportunites.
            </p>

            <p className="text-[11px] uppercase tracking-wider text-muted mb-2">Qu'est-ce qui ne va pas ?</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {FEEDBACK_TAGS.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
                    selectedTags.includes(tag.id)
                      ? 'bg-red-500/15 border-red-500/30 text-red-400'
                      : 'border-dark-border text-muted hover:text-secondary hover:border-dark-border/80'
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Commentaire optionnel..."
              className="w-full h-9 px-3 mb-4 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50"
              autoFocus
            />

            <div className="flex items-center gap-3">
              <button
                onClick={handleSubmitBad}
                disabled={selectedTags.length === 0}
                className="h-9 px-5 rounded-lg bg-carmin text-white text-xs font-medium hover:bg-carmin-light transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Envoyer
              </button>
              <button
                onClick={() => { setFeedbackState(null); setSelectedTags([]); setFeedbackComment(''); }}
                className="h-9 px-4 text-xs text-muted hover:text-secondary transition-colors"
              >
                Annuler
              </button>
              {selectedTags.length === 0 && (
                <span className="text-[11px] text-muted">Selectionnez au moins 1 raison</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────
const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [profileCollapsed, setProfileCollapsed] = useState(true);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterMakes, setFilterMakes] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [filterMinDiscount, setFilterMinDiscount] = useState(5);
  const [filterSellerType, setFilterSellerType] = useState('');
  const [filterYearMin, setFilterYearMin] = useState('');
  const [hideDismissed, setHideDismissed] = useState(true);
  const [sortBy, setSortBy] = useState('profit');

  const BATCH_SIZE = 5;

  const buildParams = useCallback(() => {
    const params = { sort_by: sortBy, limit: BATCH_SIZE, offset, min_discount_pct: filterMinDiscount };
    if (filterMakes) params.makes = filterMakes;
    if (filterPriceMax) params.price_max = parseInt(filterPriceMax, 10);
    if (filterSellerType) params.seller_type = filterSellerType;
    if (filterYearMin) params.year_min = parseInt(filterYearMin, 10);
    return params;
  }, [sortBy, offset, filterMakes, filterPriceMax, filterMinDiscount, filterSellerType, filterYearMin]);

  const loadData = useCallback(async (append = false) => {
    try {
      setLoading(true);
      setError(null);
      const [oppResult, profileResult] = await Promise.all([
        opportunityApi.getOpportunities(buildParams()),
        opportunityApi.getDealerProfile().catch(() => null),
      ]);
      const newOpps = oppResult.opportunities || [];
      setOpportunities(prev => append ? [...prev, ...newOpps] : newOpps);
      setTotal(oppResult.total || 0);
      setProfile(profileResult);
      if (oppResult.error) setError(oppResult.error);
    } catch (e) {
      setError('Impossible de charger les opportunites');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Check if all current batch opportunities have feedback
  const allFeedbackGiven = useMemo(() => {
    if (opportunities.length === 0) return false;
    return opportunities.every(o => o.user_feedback === 'good' || o.user_feedback === 'bad' || o.user_action === 'dismissed');
  }, [opportunities]);

  const hasMore = opportunities.length < total;

  const handleLoadMore = () => {
    setOffset(prev => prev + BATCH_SIZE);
  };

  // When offset changes (after initial load), append new batch
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    loadData(true);
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await opportunityApi.refreshOpportunities();
      setOffset(0);
      isInitialLoad.current = true;
      await loadData(false);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async (id) => {
    await opportunityApi.dismissOpportunity(id);
    setOpportunities(prev => prev.map(o => o.id === id ? { ...o, user_action: 'dismissed' } : o));
  };

  const handleSave = async (id) => {
    await opportunityApi.saveOpportunity(id);
    setOpportunities(prev => prev.map(o => o.id === id ? { ...o, user_action: 'saved' } : o));
  };

  const handleFeedback = async (id, feedbackData) => {
    try {
      await opportunityApi.sendFeedback(id, feedbackData);
      setOpportunities(prev => prev.map(o =>
        o.id === id ? { ...o, user_feedback: feedbackData.rating } : o
      ));
    } catch (e) {
      console.error('Feedback error:', e);
    }
  };

  const handleApplyFilters = () => {
    setOffset(0);
    isInitialLoad.current = true;
    loadData(false);
  };

  const displayedOpportunities = useMemo(() => {
    let filtered = [...opportunities];
    if (hideDismissed) filtered = filtered.filter(o => o.user_action !== 'dismissed');
    return filtered;
  }, [opportunities, hideDismissed]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <TrendingDown className="h-6 w-6 text-emerald-400" />
            Opportunites
          </h1>
          <p className="text-sm text-muted mt-1">
            Vehicules sous-cotes detectes sur le marche
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors ${
              showFilters
                ? 'bg-carmin/10 border-carmin/30 text-carmin'
                : 'border-dark-border text-secondary hover:text-primary'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filtres
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-carmin text-white text-sm font-medium hover:bg-carmin-light transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Profile card */}
      <ProfileCard
        profile={profile}
        collapsed={profileCollapsed}
        onToggle={() => setProfileCollapsed(!profileCollapsed)}
      />

      {/* Filters bar */}
      {showFilters && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-[11px] text-muted mb-1">Marques (ex: bmw,audi)</label>
              <input type="text" value={filterMakes} onChange={(e) => setFilterMakes(e.target.value)} placeholder="Toutes" className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50" />
            </div>
            <div>
              <label className="block text-[11px] text-muted mb-1">Prix max</label>
              <input type="number" value={filterPriceMax} onChange={(e) => setFilterPriceMax(e.target.value)} placeholder="Illimite" className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50" />
            </div>
            <div>
              <label className="block text-[11px] text-muted mb-1">Discount min (%)</label>
              <input type="number" value={filterMinDiscount} onChange={(e) => setFilterMinDiscount(Number(e.target.value))} min={0} max={50} className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50" />
            </div>
            <div>
              <label className="block text-[11px] text-muted mb-1">Type vendeur</label>
              <select value={filterSellerType} onChange={(e) => setFilterSellerType(e.target.value)} className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary focus:outline-none focus:border-carmin/50">
                <option value="">Tous</option>
                <option value="private">Particulier</option>
                <option value="dealer">Professionnel</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-muted mb-1">Annee min</label>
              <input type="number" value={filterYearMin} onChange={(e) => setFilterYearMin(e.target.value)} placeholder="Toutes" className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50" />
            </div>
            <div>
              <label className="block text-[11px] text-muted mb-1">Trier par</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary focus:outline-none focus:border-carmin/50">
                <option value="profit">Profit potentiel</option>
                <option value="velocity">Revente rapide</option>
                <option value="motivation">Motivation vendeur</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-border">
            <label className="flex items-center gap-2 text-xs text-secondary cursor-pointer">
              <input type="checkbox" checked={hideDismissed} onChange={(e) => setHideDismissed(e.target.checked)} className="rounded border-dark-border" />
              Masquer les opportunites ignorees
            </label>
            <button onClick={handleApplyFilters} className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-carmin text-white text-xs font-medium hover:bg-carmin-light transition-colors">
              <Search className="h-3.5 w-3.5" />
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-300">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty state */}
      {!loading && displayedOpportunities.length === 0 && (
        <div className="text-center py-16">
          <TrendingDown className="h-12 w-12 text-muted/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">Aucune opportunite trouvee</h3>
          <p className="text-sm text-muted max-w-md mx-auto">
            {total === 0
              ? "Le pipeline d'estimation AutoLink doit d'abord analyser les annonces du marche. Les opportunites apparaitront une fois les estimations disponibles."
              : "Essayez d'ajuster vos filtres pour elargir la recherche."
            }
          </p>
        </div>
      )}

      {/* Opportunity list (vertical) */}
      {!loading && displayedOpportunities.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">{total} opportunite{total > 1 ? 's' : ''} trouvee{total > 1 ? 's' : ''}</span>
          </div>

          <div className="space-y-4">
            {displayedOpportunities.map((opp) => (
              <OpportunityRow
                key={opp.id}
                opp={opp}
                onDismiss={handleDismiss}
                onSave={handleSave}
                onFeedback={handleFeedback}
              />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="text-center pt-4 space-y-2">
              {!allFeedbackGiven ? (
                <div className="text-sm text-muted bg-dark-card border border-dark-border rounded-lg px-4 py-3">
                  Evaluez les suggestions ci-dessus (Bonne / Mauvaise) pour debloquer les suivantes
                  <div className="text-xs text-muted/60 mt-1">
                    {opportunities.filter(o => o.user_feedback === 'good' || o.user_feedback === 'bad' || o.user_action === 'dismissed').length} / {opportunities.length} evaluees
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="inline-flex items-center gap-2 h-10 px-6 rounded-lg bg-carmin text-white text-sm font-medium hover:bg-carmin-light transition-colors disabled:opacity-50"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
                  Voir les 5 suivantes
                </button>
              )}
              <div className="text-xs text-muted">{opportunities.length} / {total} opportunites chargees</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Opportunities;
